import {
  NOTATIONS,
  NOTATION_LABELS,
  TRANSPOSITIONS,
  transpositionLabel,
} from '../../music/notation'
import { TUNING_GROUPS, tuningSpelling } from '../../music/tunings'
import { A4_STEP, MAX_A4, MIN_A4, useTunerStore } from '../../state/tuner-store'

/**
 * Musical settings: tuning, concert pitch, note naming and transposition.
 *
 * Every control writes straight to the store, which persists it and is read
 * live by the render loop — nothing here needs a reload to take effect.
 */
export function SettingsPanel(): React.JSX.Element {
  const capabilities = useTunerStore((state) => state.capabilities)
  const tuning = useTunerStore((state) => state.tuning)
  const a4 = useTunerStore((state) => state.a4)
  const notation = useTunerStore((state) => state.notation)
  const transposition = useTunerStore((state) => state.transposition)

  const setTuning = useTunerStore((state) => state.setTuning)
  const setA4 = useTunerStore((state) => state.setA4)
  const setNotation = useTunerStore((state) => state.setNotation)
  const setTransposition = useTunerStore((state) => state.setTransposition)

  return (
    <section className="settings" aria-label="Stimmeinstellungen">
      <label className="controls__field">
        <span>Stimmung</span>
        <select
          value={tuning.id}
          aria-label="Stimmung"
          disabled={!capabilities.tunings}
          onChange={(event) => {
            setTuning(event.target.value)
          }}
        >
          {TUNING_GROUPS.map(({ group, label, tunings }) => (
            <optgroup key={group} label={label}>
              {tunings.map((preset) => (
                <option key={preset.id} value={preset.id}>
                  {preset.name} — {tuningSpelling(preset, notation, transposition)}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
      </label>

      <label className="controls__field">
        <span>Notation</span>
        <select
          value={notation}
          aria-label="Notation"
          disabled={!capabilities.notation}
          onChange={(event) => {
            setNotation(NOTATIONS.find((n) => n === event.target.value) ?? 'english')
          }}
        >
          {NOTATIONS.map((option) => (
            <option key={option} value={option}>
              {NOTATION_LABELS[option]}
            </option>
          ))}
        </select>
      </label>

      {/* `--wide`: a slider is only as usable as it is long, and half the width
          of a phone leaves about 76 px of track for a 26 Hz range. */}
      <div className="controls__field controls__field--wide settings__pitch-field">
        <span>
          Kammerton <strong>{a4.toFixed(1)} Hz</strong>
        </span>
        <div className="settings__pitch">
          <input
            type="range"
            min={MIN_A4}
            max={MAX_A4}
            step={A4_STEP}
            value={a4}
            aria-label="Kammerton"
            disabled={!capabilities.concertPitch}
            onChange={(event) => {
              setA4(Number(event.target.value))
            }}
          />
          <input
            type="number"
            className="settings__pitch-number"
            min={MIN_A4}
            max={MAX_A4}
            step={A4_STEP}
            value={a4}
            aria-label="Kammerton in Hertz"
            disabled={!capabilities.concertPitch}
            onChange={(event) => {
              const parsed = Number(event.target.value)
              if (Number.isFinite(parsed)) setA4(parsed)
            }}
          />
        </div>
      </div>

      <label className="controls__field">
        <span>Transposition</span>
        <select
          value={transposition}
          aria-label="Transposition"
          aria-describedby="transposition-hint"
          disabled={!capabilities.transposition}
          onChange={(event) => {
            setTransposition(Number(event.target.value))
          }}
        >
          {TRANSPOSITIONS.map((semitones) => (
            <option key={semitones} value={semitones}>
              {transpositionLabel(semitones, notation)}
              {semitones === 0
                ? ' (Konzertstimmung)'
                : ` (${semitones > 0 ? '+' : ''}${String(semitones)})`}
            </option>
          ))}
        </select>
        {/* `--aside` marks this as the one line the bottom sheet may drop from
            sight when a phone has no room for it: it explains a setting rather
            than telling the player something about their instrument, and it
            stays in the accessibility tree either way. */}
        <small id="transposition-hint" className="controls__hint controls__hint--aside">
          Für transponierende Instrumente: ändert nur die angezeigten Notennamen, nie die gemessene
          Frequenz.
        </small>
      </label>

      {/* Shown rather than hidden: the locked controls are the clearest
          description of what the app adds, and hiding them would only make
          the demo look like it can do less than it does. */}
      {!capabilities.tunings && (
        <p className="settings__locked" role="note">
          <strong>Diese Demo stimmt in E&nbsp;Standard bei 440&nbsp;Hz.</strong> Neun Stimmungen,
          Kammerton 428–454&nbsp;Hz, Notationen, Transposition und die gezupften Referenztöne gibt
          es in der App für macOS, Windows, iOS und Android.
        </p>
      )}
    </section>
  )
}
